import { z } from 'zod';

/**
 * JSON 可序列化的 value / object 型別 — 從 `type-fest` re-export。
 *
 * 為什麼需要這組型別（與 TS 內建型別的差異）：
 *   apiFetch 系列（operator / admin / ...）的 body 預期是「可 JSON.stringify 的物件」。
 *   TS 內建沒有 `JsonObject` / `JsonValue`，常用替代品都有 quirk：
 *     - `object`：太寬，接受 Date / function / class instance，serialize 結果不對
 *     - `Record<string, unknown>`：sealed interface（typed Input）不能 assign，因 TS
 *       要求 explicit index signature 承諾；且 value 是 unknown 比實際 JSON 寬
 *
 *   `type-fest` 的 `JsonObject` 透過 recursive type 同時解決兩者：
 *     - 顯式 index sig（typed Input `extends JsonObject` 後即可 assign）
 *     - value 限定為 `JsonValue`（排除 Date / function；string / number / boolean / null / 巢狀均可）
 *
 * 使用慣例：
 *   - apiFetch body 型別：union `JsonObject | Record<string, unknown>`（前者接 typed Input、後者接 dynamic Hash）
 *   - typed Input interface（送 body 用）：`interface FooInput extends JsonObject { ... }`
 *   - jsonb 欄位在 response / state（display 用）：`Record<string, unknown>` — schema-less、強迫 narrowing
 *   - jsonb 欄位在 body payload 內：`JsonObject`（滿足 parent 的 `extends JsonObject` 鏈）
 */
export type { JsonValue, JsonObject, JsonArray, JsonPrimitive } from 'type-fest';

// 對應 app/controllers/concerns/api_response.rb#render_success
//   render json: { success: true, message: ..., data: <data>, **extra }
//
// `success` 與 `message` 是 hardcoded — render_success / render_error / render_model_errors
// 一律會送，從不省略。所以這兩個欄位 required。
//
// passthrough() 讓後端多回的欄位（如 since_id、total_count、不同 endpoint 的 extra）
// 不會 throw，但也不會出現在 TS type 上 — drift 由「漏欄位 / 型別不對」觸發，新增欄位視為 forward-compat。

/**
 * 包成 `{ success, message, data: T }` envelope。
 * 用於 render_data_item — 單筆資料回應。
 *
 * `success` 用 `z.literal(true)` 是刻意為之 — 跟 `ApiErrorEnvelope.success: false`
 * 組成 discriminated union，caller 寫 `if (r.success) { r.data... } else { r.errors... }`
 * 才能在 TS 完整 narrow，免去 cast 與 narrow trick。
 */
export const apiSuccess = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    message: z.string(),
    data: dataSchema,
  }).passthrough();

/**
 * 包成 `{ success, message, data: T[], has_more, ... }` envelope。
 * 用於 render_data_list / render_array_list — 列表回應。
 */
export const apiList = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    success: z.literal(true),
    message: z.string(),
    data: z.array(itemSchema),
    has_more: z.boolean(),
  }).passthrough();

/**
 * 後端錯誤欄位明細的 shape（對齊 `ApiResponse#format_model_errors`）。
 * 由 `errors.add(:field, :code, k1: v1, ...)` 攤平 — `message` 是 error code，
 * `interpolations` 是除 `:error` 之外的 keyword args。
 */
export type ApiErrorDetail = {
  message: string;
  interpolations?: Record<string, unknown>;
};

/**
 * 後端錯誤回應的 envelope shape（對齊 BACKEND.md §6 + ApiResponse#render_error）。
 *
 * `message` 永遠存在（Error Code，snake_case），無 `data`。`interpolations` 內層用
 * `unknown` 是因 i18n 插值變數型別多樣，交給 `@utils/apiErrorHandler` 處理。
 */
export type ApiErrorEnvelope = {
  success: false;
  message: string;
  errors?: Record<string, ApiErrorDetail[]>;
  interpolations?: Record<string, unknown>;
};

// Shared post-decode classification logic for both parseApiResponse and parseAdminApiResponse.
function classifyEnvelope<T extends z.ZodTypeAny>(
  body: unknown,
  res: Response,
  schema: T,
): z.infer<T> | ApiErrorEnvelope {
  const isErrorEnvelope =
    body !== null && typeof body === 'object' && !Array.isArray(body)
    && (body as { success?: unknown }).success === false;
  if (!res.ok || isErrorEnvelope) {
    if (body === null || typeof body !== 'object' || Array.isArray(body)) {
      throw new Error(`Malformed error body from ${res.url} (status ${res.status})`);
    }
    return body as ApiErrorEnvelope;
  }
  return schema.parse(body) as z.infer<T>;
}

/**
 * Service 層的 schema parse 包裝。
 *
 * `apiSuccess` / `apiList` 都要求 `data` 必填，但 422 envelope 沒有 `data`。若 service
 * 對所有回應無條件 `schema.parse(body)`，422 會爆 ZodError，caller 的 `catch` 把它
 * 當網路錯誤處理，後端 `errors` 整包消失。
 *
 * Scope：**僅在 `services/operator/*` 使用**。`operatorApiFetch` 已 throw 所有
 * 非 (2xx | 422) 狀態，body 由 `Api::ApplicationController#render_*` 保證 well-formed，
 * 因此這個 helper 不需要處理非 JSON body。
 * `services/admin/*` 請改用 `parseAdminApiResponse`（adminApiFetch 5xx/404 raw return，
 * body 可能是 HTML，需要額外 guard）。
 */
export async function parseApiResponse<T extends z.ZodTypeAny>(
  res: Response,
  schema: T,
): Promise<z.infer<T> | ApiErrorEnvelope> {
  const body = await res.json();
  return classifyEnvelope(body, res, schema);
}

/**
 * Variant of parseApiResponse for admin services.
 *
 * adminApiFetch (services/admin/apiService.js) lets 5xx / 404 raw return — it does
 * NOT throw like operatorApiFetch. So the response body for non-2xx-non-422 may be
 * HTML or plain text. Without an extra guard, `await res.json()` here would throw
 * a confusing SyntaxError far from the actual point of failure.
 *
 * Envelope detection semantics match parseApiResponse otherwise:
 *   - 2xx with `{ success: true, ... }` envelope → schema-parsed data
 *   - any response with `{ success: false, ... }` envelope OR non-2xx with envelope body
 *     → passthrough as ApiErrorEnvelope (preserves backend error code + interpolations)
 *   - non-JSON body → throw `Non-JSON body from <url> (status N)`
 *   - non-(2xx|422) with non-object body → throw `Malformed error body from <url> (status N)`
 */
export async function parseAdminApiResponse<T extends z.ZodTypeAny>(
  res: Response,
  schema: T,
): Promise<z.infer<T> | ApiErrorEnvelope> {
  let body: unknown;
  try {
    body = await res.json();
  } catch (err) {
    throw new Error(`Non-JSON body from ${res.url} (status ${res.status})`, { cause: err });
  }
  return classifyEnvelope(body, res, schema);
}
